import { useMemo, type ReactElement } from "react";

import { useLocation } from "@tanstack/react-router";
import { IconArchive, IconFileListReport, IconFolderOpen, IconFolderProjects, IconQuestion } from "@synthesis/shared-icons";

import { useQueryProject } from "../../../../entity/project";

const v4 = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);

interface BreadcrumbItem {
  id: number;
  title: string;
  path: string;
  leftIcon?: ReactElement;
  isProject?: string;
}

const breadcrumbNameMap: Record<string, BreadcrumbItem> = {
  "/project/[uuid1]": { id: 2, title: "Карточка проекта", path: "/project/[uuid1]", isProject: "/dashboard" },
  "/project/[uuid1]/filesystem": { id: 3, title: "Трехмерные модели", path: "/project/[uuid1]/filesystem" },
  "/project/[uuid1]/filesystem/models": { id: 31, title: "Трехмерные модели", path: "/project/[uuid1]/filesystem/models" },
  "/project/[uuid1]/filesystem/docs": { id: 32, title: "Документация", path: "/project/[uuid1]/filesystem/docs" },
  "/project/[uuid1]/filesystem/drawings": { id: 36, title: "Коллекция чертежей", path: "/project/[uuid1]/filesystem/drawings" },
  "/project/[uuid1]/role": { id: 4, title: "Список экспертов", path: "/project/[uuid1]/role" },
  "/projects/add-project": { id: 10, title: "Добавление проекта", path: "projects/add-project" },
  "/archiveProject/[uuid1]": { id: 22, title: "Карточка проекта", path: "/archiveProject/[uuid1]", isProject: "/dashboard" },
  "/archiveProject/[uuid1]/filesystem": { id: 33, title: "Трехмерные модели", path: "/archiveProject/[uuid1]/filesystem" },
  "/archiveProject/[uuid1]/filesystem/models": { id: 34, title: "Трехмерные модели", path: "/archiveProject/[uuid1]/filesystem/models" },
  "/archiveProject/[uuid1]/filesystem/docs": { id: 35, title: "Документация", path: "/archiveProject/[uuid1]/filesystem/docs" },
  "/archiveProject/[uuid1]/filesystem/drawings": { id: 37, title: "Коллекция чертежей", path: "/archiveProject/[uuid1]/filesystem/drawings" },
  "/archiveProject/[uuid1]/role": { id: 4, title: "Список экспертов", path: "/archiveProject/[uuid1]/role" },
};

export const useBreadcrumbs = () => {
  const location = useLocation();
  const match = location.pathname.includes("project")
    ? location.pathname.match(/\/project\/([^/]+)/)
    : location.pathname.match(/\/archiveProject\/([^/]+)/);
  const projectId = match ? match[1] : "";
  const { project } = useQueryProject(projectId ?? "") || { project: null, isLoading: false };

  return useMemo(() => {
    const pathSnippets = location.pathname.split("/").filter((i) => i);
    const extraBreadcrumbItems = pathSnippets.map((_, index) => `/${pathSnippets.slice(0, index + 1).join("/")}`);

    const resultBreadcrumbs = extraBreadcrumbItems.map((breadcrumb) => {
      const uuid: string[] = [];
      const result = breadcrumb.split("/").map((cuttedLink) => {
        if (v4.test(cuttedLink)) {
          uuid.push(cuttedLink);
          return `[uuid${uuid.length}]`;
        }
        return cuttedLink;
      });

      const findBreacrumbResult = breadcrumbNameMap[result.join("/")];
      if (findBreacrumbResult) {
        if (findBreacrumbResult.isProject) {
          findBreacrumbResult.path = breadcrumb + findBreacrumbResult.isProject;
        } else {
          findBreacrumbResult.path = breadcrumb;
        }
        return findBreacrumbResult;
      }
    });

    const filtered = resultBreadcrumbs.filter((x) => x !== undefined) as BreadcrumbItem[];

    const reportsChildrenMap: Record<string, string> = {
      "statistics-usage-software": "Статистика по использованию ПО",
      "detailed-file-report": "Детальный отчёт по файлам",
    };

    switch (true) {
      case location.pathname === "/":
        return [{ id: 111, title: "Проекты", path: "/", leftIcon: <IconFolderProjects className="size-4" /> }, ...filtered];
      case location.pathname.includes("/project/"):
        return [
          { id: 112, title: "Проекты", path: "/", leftIcon: <IconFolderOpen className="size-4" /> },
          { id: 113, title: project.title ?? "", path: `/project/${project.id}/dashboard` },
          ...filtered,
        ];
      case location.pathname === "/archivedProjects":
        return [{ id: 114, title: "Архив проектов", path: "/archiveProject", leftIcon: <IconArchive className="size-4" /> }, ...filtered];
      case location.pathname.includes("/archiveProject/"):
        return [
          { id: 115, title: "Архив проектов", path: "/archivedProjects", leftIcon: <IconFolderOpen className="size-4" /> },
          { id: 116, title: project.title ?? "", path: `/archiveProject/${project.id}/dashboard` },
          ...filtered,
        ];
      case location.pathname.includes("/help"):
        return [{ id: 120, title: "Помощь", path: "/help", leftIcon: <IconQuestion className="size-4" /> }, ...filtered];
      case location.pathname === "/reports":
        return [{ id: 130, title: "Отчёты", path: "/reports", leftIcon: <IconFileListReport className="size-4" /> }, ...filtered];
      case location.pathname.startsWith("/reports"): {
        const base = { id: 130, title: "Отчёты", path: "/reports", leftIcon: <IconFileListReport className="size-4" /> };
        const segments = location.pathname.split("/").filter(Boolean);
        if (segments.length === 1) return [base, ...filtered];
        const childKey = segments[1];
        const childTitle = childKey ? reportsChildrenMap[childKey] : undefined;
        if (childTitle) {
          return [base, { id: 131, title: childTitle, path: `/reports/${childKey}` }, ...filtered];
        }
        return [base, ...filtered];
      }
      default:
        return [...filtered];
    }
  }, [location.pathname, project]);
};
